import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useIsMobile } from '../../hooks/useMediaQuery.js';
import { InstallHint } from '../../features/pwa/InstallHint.js';
import { TopNav } from './TopNav.js';
import { Sidebar } from './Sidebar.js';

export interface AppShellProps {
  readonly children: ReactNode;
}

export function AppShell({ children }: AppShellProps): JSX.Element {
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [swipeX, setSwipeX] = useState(0);
  const swipeRef = useRef<{ x: number; y: number; determined: boolean; active: boolean } | null>(null);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => { setDrawerOpen(false); }, [pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawerOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const onSwipeDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse') return;
    if (!drawerOpen && e.clientX > 30) return; // only left-edge swipe to open
    swipeRef.current = { x: e.clientX, y: e.clientY, determined: false, active: true };
  };

  const onSwipeMove = (e: React.PointerEvent) => {
    if (!swipeRef.current?.active) return;
    const dx = e.clientX - swipeRef.current.x;
    const dy = e.clientY - swipeRef.current.y;
    if (!swipeRef.current.determined) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      if (Math.abs(dy) > Math.abs(dx)) { swipeRef.current.active = false; setSwipeX(0); return; }
      swipeRef.current.determined = true;
    }
    setSwipeX(drawerOpen ? Math.min(0, dx) : Math.max(0, dx));
  };

  const onSwipeUp = () => {
    if (!swipeRef.current?.determined) { swipeRef.current = null; setSwipeX(0); return; }
    if (!drawerOpen && swipeX > 80)  setDrawerOpen(true);
    if (drawerOpen  && swipeX < -80) setDrawerOpen(false);
    swipeRef.current = null;
    setSwipeX(0);
  };

  return (
    <div
      className="min-h-dvh w-screen bg-bg text-text overflow-x-hidden"
      onPointerDown={isMobile ? onSwipeDown : undefined}
      onPointerMove={isMobile ? onSwipeMove : undefined}
      onPointerUp={isMobile ? onSwipeUp : undefined}
      onPointerCancel={isMobile ? () => { swipeRef.current = null; setSwipeX(0); } : undefined}
    >
      <TopNav onMenuClick={isMobile ? () => setDrawerOpen((o) => !o) : undefined} />

      {isMobile ? (
        <>
          <main
            style={{
              paddingTop: 'calc(3.5rem + env(safe-area-inset-top))',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            <InstallHint />
            {children}
          </main>
          <MobileDrawer open={drawerOpen} swipeX={swipeX} onClose={() => setDrawerOpen(false)} />
        </>
      ) : (
        <div
          className="grid min-h-dvh"
          style={{
            gridTemplateColumns: '220px 1fr',
            paddingTop: 'calc(3.5rem + env(safe-area-inset-top))',
          }}
        >
          <Sidebar />
          <main className="overflow-y-auto">
            <InstallHint />
            {children}
          </main>
        </div>
      )}
    </div>
  );
}

function MobileDrawer({
  open,
  swipeX,
  onClose,
}: {
  readonly open: boolean;
  readonly swipeX: number;
  readonly onClose: () => void;
}): JSX.Element {
  const isSwiping = swipeX !== 0;
  // Translate: closed=-100%+swipeX, open=0+swipeX (clamped so it doesn't open past 0)
  const translateX = open
    ? `${Math.min(0, swipeX)}px`
    : `calc(-100% + ${Math.max(0, swipeX)}px)`;
  // Backdrop opacity follows swipe progress
  const drawerWidth = Math.min(0.85 * window.innerWidth, 288);
  const progress = open
    ? 1 + swipeX / drawerWidth      // 1 → 0 as swipeX → -drawerWidth
    : swipeX / drawerWidth;          // 0 → 1 as swipeX → drawerWidth
  const backdropOpacity = Math.max(0, Math.min(1, progress));

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden={!open && swipeX === 0}
        onClick={onClose}
        style={{ opacity: backdropOpacity }}
        className={[
          'fixed inset-0 z-30 bg-black/60',
          !isSwiping ? 'transition-opacity duration-200' : '',
          open || swipeX > 0 ? 'pointer-events-auto' : 'pointer-events-none',
        ].join(' ')}
      />
      {/* Panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        style={{
          top: 'calc(3.5rem + env(safe-area-inset-top))',
          paddingBottom: 'env(safe-area-inset-bottom)',
          transform: `translateX(${translateX})`,
          transition: isSwiping ? 'none' : 'transform 200ms ease-out',
        }}
        className="fixed bottom-0 left-0 z-40 w-72 max-w-[85vw] bg-surface border-r border-border"
      >
        <Sidebar />
      </aside>
    </>
  );
}
